import type { RealtimeAudioError, RealtimeAudioState } from "../hooks/use-realtime-audio"

export type VoiceRingState =
  | "idle"
  | "connecting"
  | "listening"
  | "thinking"
  | "speaking"
  | "tool"
  | "interrupted"
  | "reconnecting"
  | "error"

type ResolveVoiceRingStateOptions = {
  state: RealtimeAudioState
  isPlayingAudio?: boolean
  error?: RealtimeAudioError | null
}

/**
 * Collapse transport-level voice states into the nine deterministic visual
 * modes used by the voice ring. Playback wins over a late `listening` event so
 * the UI stays in speaking mode until the final buffered audio has finished.
 * The two GPT-Live transport states outrank playback instead: both have
 * already flushed the queue, and a stale `isPlayingAudio` must not hide them.
 */
export function resolveVoiceRingState({
  state,
  isPlayingAudio = false,
  error,
}: ResolveVoiceRingStateOptions): VoiceRingState {
  if (error) return "error"
  if (state === "reconnecting") return "reconnecting"
  if (state === "interrupted") return "interrupted"
  if (isPlayingAudio || state === "ai_speaking") return "speaking"

  switch (state) {
    case "connecting":
      return "connecting"
    case "listening":
    case "user_speaking":
      return "listening"
    case "processing":
      return "thinking"
    case "using_tools":
      return "tool"
    default:
      return "idle"
  }
}

export function isAudioReactiveRingState(state: VoiceRingState): boolean {
  return state === "listening" || state === "speaking"
}
